import { Link } from 'react-router-dom';
import { GlowCard } from '../components/GlowCard.js';

const platforms = [
  { name: 'TikTok', cls: 'border-white/10 bg-white/5 text-white/70' },
  { name: 'YouTube', cls: 'border-red-500/30 bg-red-500/10 text-red-400' },
  { name: 'Instagram', cls: 'border-pink-500/30 bg-pink-500/10 text-pink-400' },
  { name: 'Facebook', cls: 'border-blue-500/30 bg-blue-500/10 text-blue-400' },
  { name: 'LinkedIn', cls: 'border-sky-500/30 bg-sky-500/10 text-sky-400' },
];

const steps = [
  {
    number: '01',
    title: 'Connect TikTok',
    description: 'Link your TikTok account and Cascade monitors your content automatically.',
  },
  {
    number: '02',
    title: 'AI rewrites for each platform',
    description: 'Claude rewrites your copy to match each platform voice, character limits, and audience.',
  },
  {
    number: '03',
    title: 'Auto-publish everywhere',
    description: 'Content goes live on YouTube, Instagram, Facebook, and LinkedIn without lifting a finger.',
  },
];

export default function LandingPage() {
  return (
    <div className="min-h-screen bg-pix-void text-white">
      {/* Nav */}
      <header className="border-b border-white/5 px-6 py-4">
        <div className="mx-auto flex max-w-6xl items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold tracking-widest text-white/30 uppercase">PIX3L</span>
            <span className="text-white/10">/</span>
            <span className="text-sm font-bold tracking-tight text-white">Cascade</span>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to="/login"
              className="text-sm font-medium text-white/50 hover:text-white transition-colors"
            >
              Sign In
            </Link>
            <Link
              to="/signup"
              className="rounded-lg bg-pix-pink px-4 py-2 text-sm font-semibold text-white hover:bg-pix-pink-dark transition-colors"
            >
              Get Started Free
            </Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="px-6 py-28 text-center">
        <div className="mx-auto max-w-4xl">
          <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-pix-pink/30 bg-pix-pink/10 px-4 py-1.5 text-xs font-semibold tracking-widest text-pix-pink uppercase">
            Powered by Claude AI
          </div>
          <h1 className="mt-2 text-6xl font-extrabold tracking-tight sm:text-7xl">
            <span className="bg-gradient-to-r from-pix-pink to-pix-blue bg-clip-text text-transparent">
              CASCADE.
            </span>
            <br />
            <span className="text-white">One Pipeline. Every Platform.</span>
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-white/40">
            Cascade takes your TikTok videos, rewrites the copy for every platform with AI, and auto-publishes to YouTube, Instagram, Facebook, and LinkedIn. Zero manual republishing.
          </p>
          <div className="mt-10 flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/signup"
              className="w-full rounded-xl bg-pix-pink px-8 py-4 text-base font-semibold text-white shadow-[0_0_40px_rgba(233,30,140,0.35)] hover:bg-pix-pink-dark transition-colors sm:w-auto"
            >
              Get Started Free
            </Link>
            <Link
              to="/login"
              className="w-full rounded-xl border border-white/10 px-8 py-4 text-base font-semibold text-white/60 hover:bg-white/5 transition-colors sm:w-auto"
            >
              Sign In
            </Link>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="border-y border-white/5 px-6 py-10">
        <div className="mx-auto grid max-w-3xl grid-cols-3 gap-8 text-center">
          {[
            { value: '5', label: 'Platforms supported' },
            { value: '15m', label: 'Auto-sync interval' },
            { value: '100%', label: 'Hands-free publishing' },
          ].map((stat) => (
            <div key={stat.label}>
              <div className="text-3xl font-extrabold text-pix-pink">{stat.value}</div>
              <div className="mt-1 text-xs text-white/30 uppercase tracking-wider">{stat.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section className="px-6 py-24">
        <div className="mx-auto max-w-5xl">
          <h2 className="mb-2 text-center text-3xl font-bold text-white">How It Works</h2>
          <p className="mb-14 text-center text-sm text-white/30 uppercase tracking-wider">Three steps. Zero friction.</p>
          <div className="grid gap-6 sm:grid-cols-3">
            {steps.map((step) => (
              <GlowCard
                key={step.number}
                className="rounded-2xl border border-white/5 bg-pix-deep p-8"
              >
                <div className="mb-4 text-4xl font-extrabold text-pix-pink/20">
                  {step.number}
                </div>
                <h3 className="mb-2 text-base font-semibold text-white">{step.title}</h3>
                <p className="text-sm leading-relaxed text-white/40">{step.description}</p>
              </GlowCard>
            ))}
          </div>
        </div>
      </section>

      {/* Platforms */}
      <section className="px-6 py-20 text-center">
        <div className="mx-auto max-w-3xl">
          <h2 className="mb-2 text-3xl font-bold text-white">Every Platform. Instantly.</h2>
          <p className="mb-10 text-white/40">
            One piece of content. Five platforms. All handled automatically.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {platforms.map((p) => (
              <span
                key={p.name}
                className={`rounded-full border px-6 py-2.5 text-sm font-semibold ${p.cls}`}
              >
                {p.name}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 py-24 text-center">
        <GlowCard className="mx-auto max-w-2xl rounded-2xl border border-pix-pink/20 bg-pix-deep p-16 shadow-[0_0_80px_rgba(233,30,140,0.08)]">
          <h2 className="mb-4 text-4xl font-extrabold text-white">
            Ready to build your{' '}
            <span className="bg-gradient-to-r from-pix-pink to-pix-blue bg-clip-text text-transparent">
              content engine?
            </span>
          </h2>
          <p className="mb-8 text-white/40">
            Connect TikTok, build your first pipeline, and watch your content reach every platform automatically.
          </p>
          <Link
            to="/signup"
            className="inline-block rounded-xl bg-pix-pink px-10 py-4 text-base font-semibold text-white shadow-[0_0_40px_rgba(233,30,140,0.3)] hover:bg-pix-pink-dark transition-colors"
          >
            Get Started Free
          </Link>
        </GlowCard>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/5 px-6 py-8">
        <div className="mx-auto flex max-w-6xl flex-col items-center gap-4 sm:flex-row sm:justify-between">
          <div className="flex items-center gap-2 text-xs text-white/20">
            <span className="font-bold tracking-widest uppercase">PIX3L</span>
            <span>/</span>
            <span>Cascade</span>
          </div>
          <div className="flex gap-6 text-xs text-white/20">
            <Link to="/terms" className="hover:text-white/50 transition-colors">Terms</Link>
            <Link to="/privacy" className="hover:text-white/50 transition-colors">Privacy</Link>
            <Link to="/login" className="hover:text-white/50 transition-colors">Sign In</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
